import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-easy-crop';

const loadImage = (src) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });

async function getCroppedBlob(src, area, rotation, outW, outH) {
  const img = await loadImage(src);

  // ابعاد تصویر بعد از چرخش (در ۹۰ و ۲۷۰ درجه طول و عرض عوض می‌شود)
  const swap = rotation % 180 !== 0;
  const bw = swap ? img.naturalHeight : img.naturalWidth;
  const bh = swap ? img.naturalWidth : img.naturalHeight;

  // ۱) تصویر چرخیده روی یک بوم میانی (شفافیت حفظ می‌شود)
  const rotated = document.createElement('canvas');
  rotated.width = bw;
  rotated.height = bh;
  const rctx = rotated.getContext('2d');
  rctx.translate(bw / 2, bh / 2);
  rctx.rotate((rotation * Math.PI) / 180);
  rctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  // ۲) برش از روی تصویر چرخیده
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';

  const scaleX = outW / area.width;
  const scaleY = outH / area.height;

  ctx.drawImage(
    rotated,
    0,
    0,
    bw,
    bh,
    -area.x * scaleX,
    -area.y * scaleY,
    bw * scaleX,
    bh * scaleY
  );

  // PNG تا شفافیت (مهر/امضا) حفظ شود؛ سرور به WebP تبدیل می‌کند
  return new Promise((res) => canvas.toBlob(res, 'image/png'));
}

export default function CropModal({ src, aspect, outW, outH, title, onCancel, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // 0 | 90 | 180 | 270
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_, pixels) => setArea(pixels), []);

  const rotate = (delta) => {
    setRotation((r) => (r + delta + 360) % 360);
    setCrop({ x: 0, y: 0 });
  };

  const confirm = async () => {
    if (!area) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(src, area, rotation, outW, outH);
      onConfirm(blob);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="crop-overlay" onMouseDown={onCancel}>
      <div className="crop-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="crop-head">
          <h3>{title}</h3>
          <button type="button" className="crop-x" onClick={onCancel}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="crop-area">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            minZoom={0.3}
            maxZoom={4}
            restrictPosition={false}
            objectFit="contain"
            showGrid
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
          />
        </div>

        <div className="crop-tools">
          <button
            type="button"
            className="crop-rot"
            onClick={() => rotate(-90)}
            title="چرخش ۹۰ درجه به چپ"
            aria-label="چرخش ۹۰ درجه به چپ"
          >
            <i className="fa-solid fa-rotate-left" />
          </button>
          <button
            type="button"
            className="crop-rot"
            onClick={() => rotate(90)}
            title="چرخش ۹۰ درجه به راست"
            aria-label="چرخش ۹۰ درجه به راست"
          >
            <i className="fa-solid fa-rotate-right" />
          </button>

          <div className="crop-zoom">
            <i className="fa-solid fa-magnifying-glass-minus" />
            <input
              type="range"
              min={0.3}
              max={4}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
            <i className="fa-solid fa-magnifying-glass-plus" />
          </div>
        </div>

        <p className="crop-note">
          خروجی: {outW}×{outH} پیکسل — تصویر را جابه‌جا کنید، با اسلایدر زوم کنید و با دکمه‌ها بچرخانید
        </p>

        <div className="flexend">
          <button type="button" className="btn secondary" onClick={onCancel}>
            انصراف
          </button>
          <button type="button" className="btn" onClick={confirm} disabled={busy}>
            <i className="fa-solid fa-crop-simple" />
            {busy ? 'در حال برش...' : 'برش و آپلود'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}